/*
 * ██████╗ ██╗████████╗███████╗ █████╗  ██████╗████████╗ ██████╗ ██████╗ ██╗   ██╗
 * ██╔══██╗██║╚══██╔══╝██╔════╝██╔══██╗██╔════╝╚══██╔══╝██╔═══██╗██╔══██╗╚██╗ ██╔╝
 * ██████╔╝██║   ██║   █████╗  ███████║██║        ██║   ██║   ██║██████╔╝ ╚████╔╝
 * ██╔══██╗██║   ██║   ██╔══╝  ██╔══██║██║        ██║   ██║   ██║██╔══██╗  ╚██╔╝
 * ██████╔╝██║   ██║   ██║     ██║  ██║╚██████╗   ██║   ╚██████╔╝██║  ██║   ██║
 * ╚═════╝ ╚═╝   ╚═╝   ╚═╝     ╚═╝  ╚═╝ ╚═════╝   ╚═╝    ╚═════╝ ╚═╝  ╚═╝   ╚═╝
 *
 *
 * Copyright (c) 2021 Bitfactory GmbH. All rights reserved.
 * https://www.bitfactory.io
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are not permitted.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS
 * "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT
 * LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS
 * FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE
 * COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT,
 * INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES INCLUDING,
 * BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES;
 * LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER
 * CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT
 * LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN
 * ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE
 * POSSIBILITY OF SUCH DAMAGE.
 */

import { observer } from 'mobx-react';
import React, { ReactElement } from 'react';
import { Box, Button, Divider, Grid, Paper } from '@material-ui/core';
import { Image, ThumbDown, ThumbUp } from '@material-ui/icons';
import EntryBoxViewStyles from './EntryBoxViewStyles';
import { Entry, ENTRY_REVIEW_STATE } from '../../../../../types/course/Entry';
import clsx from 'clsx';
import { EntriesReviewViewModel } from '../EntriesReviewViewModel';

interface Props {
	entry: Entry;
	viewModel: EntriesReviewViewModel;
}

const EntryBoxView = observer((props: Props): ReactElement => {
	const classes = EntryBoxViewStyles();
	const { entry, viewModel } = props;

	return (
		<Grid item md={3} xs={4}>
			<Paper>
				<Box
					onClick={() => viewModel.setEntry(entry)}
					className={clsx(classes.image, {
						[classes.approved]: entry.state === ENTRY_REVIEW_STATE.APPROVED,
						[classes.rejected]: entry.state === ENTRY_REVIEW_STATE.REJECTED,
					})}>
					{entry.imageUrl ? (
						<img src={entry.imageUrl} alt='' />
					) : (
						<Box className={classes.placeholder} display={'flex'} justifyContent={'center'} alignItems={'center'}>
							<Image />
						</Box>
					)}
				</Box>
				<Grid container alignItems={'center'}>
					{entry.state === ENTRY_REVIEW_STATE.PENDING && (
						<>
							<Grid className={classes.thumbBox} item xs>
								<Button
									fullWidth={true}
									onClick={() => viewModel.updateReviewState(entry.id, ENTRY_REVIEW_STATE.REJECTED)}>
									<ThumbDown />
								</Button>
							</Grid>
							<Divider className={classes.divider} orientation='vertical' flexItem />
							<Grid className={classes.thumbBox} item xs>
								<Button
									fullWidth={true}
									onClick={() => viewModel.updateReviewState(entry.id, ENTRY_REVIEW_STATE.APPROVED)}>
									<ThumbUp />
								</Button>
							</Grid>
						</>
					)}
					{entry.state === ENTRY_REVIEW_STATE.APPROVED && (
						<Box className={`${classes.thumbBox} ${classes.thumbApproved}`}>
							<ThumbUp />
						</Box>
					)}
					{entry.state === ENTRY_REVIEW_STATE.REJECTED && (
						<Box className={`${classes.thumbBox} ${classes.thumbRejected}`}>
							<ThumbDown />
						</Box>
					)}
				</Grid>
			</Paper>
		</Grid>
	);
});

export default EntryBoxView;
